import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { toast } from 'src/@/components/ui/use-toast';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from 'src/@/components/ui/dropdown-menu';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'src/@/components/ui/select';
import { Input } from 'src/@/components/ui/input';
import { resolvePlatform } from 'src/design/tokens';
import mktPostsService from 'src/services/mktPostsService';
import mktAccountsService from 'src/services/mktAccountsService';
import { MKT_POSTS_DESIGN as d } from './MktPosts.design';
import { STATUS_MAP } from './constants';
import MktPostsTable from './MktPostsTable';
import MktPostsGrid from './MktPostsGrid';
import MktPostsCalendar from './MktPostsCalendar';
import MktPostDeleteDialog from './MktPostDeleteDialog';
import MktPostsBulkDeleteDialog from './MktPostsBulkDeleteDialog';
import MktPostPreviewModal from './MktPostPreviewModal';
import PublishScopeBanner from './components/PublishScopeBanner';
import PostsConnectHero from './components/PostsConnectHero';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';

const BASE_URL = '/dashboard/marketing/publicacoes';
const POST_PROVIDERS = new Set(['instagram', 'facebook']);

export default function MktPosts() {
    const { t } = useTranslation('marketing');
    const { user } = useAuth();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(true);
    const [capabilities, setCapabilities] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid');

    const [statusFilter, setStatusFilter] = useState('');
    const [platformFilter, setPlatformFilter] = useState('');
    const [accountFilter, setAccountFilter] = useState('');
    const [search, setSearch] = useState('');

    // Paginacao da visao Lista — client-side (mesmo padrao de Rastreamento):
    // fetchAll ja traz todos os posts que batem no filtro, so fatia pra
    // exibicao. Grid usa carregamento sob demanda (scroll), nao paginas.
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [autoSyncing, setAutoSyncing] = useState(false);
    const [previewPost, setPreviewPost] = useState(null);
    const syncLock = useRef(false);

    const hasActiveFilters = Boolean(statusFilter || platformFilter || accountFilter || search);
    const autoSyncStorageKey = `lais:mkt-posts-auto-sync:${user.clienteId}`;

    // Selecao para exclusao em massa — vale sobre TODO o conjunto filtrado
    // (posts), nao so a pagina atual da Lista, entao "selecionar todos" tem o
    // mesmo efeito na Lista e no Grid.
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDeleteMode, setBulkDeleteMode] = useState(null); // 'selection' | 'account' | null
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const fetchAll = useCallback(async ({ soft = false } = {}) => {
        if (!soft) setLoading(true);
        try {
            const [postsRes, capsRes] = await Promise.all([
                mktPostsService.listar(user.clienteId, {
                    status: statusFilter || undefined,
                    platform: platformFilter || undefined,
                    account_id: accountFilter || undefined,
                    search: search || undefined
                }),
                mktPostsService.capacidades(user.clienteId).catch(() => null)
            ]);
            setPosts(postsRes?.data || []);
            setCapabilities(capsRes?.data || null);
        } catch (err) {
            console.error('Erro ao carregar publicações:', err);
            if (!soft) {
                toast({ title: t('common.error'), description: t('posts.toast.loadFailed'), variant: 'destructive' });
            }
        } finally {
            if (!soft) setLoading(false);
        }
    }, [user.clienteId, statusFilter, platformFilter, accountFilter, search, t]);

    const postAccounts = useMemo(
        () => accounts.filter((acc) => POST_PROVIDERS.has(acc.provider)),
        [accounts]
    );

    const filteredAccount = useMemo(
        () => postAccounts.find((acc) => acc._id === accountFilter) || null,
        [postAccounts, accountFilter]
    );

    const semContaParaPosts = !accountsLoading && postAccounts.length === 0;

    // Contas conectadas, pro filtro "Conta" — lista independente dos filtros
    // de posts (nao precisa refetch a cada busca/status/plataforma).
    useEffect(() => {
        setAccountsLoading(true);
        mktAccountsService.listar(user.clienteId)
            .then((res) => setAccounts(res?.data || []))
            .catch(() => setAccounts([]))
            .finally(() => setAccountsLoading(false));
    }, [user.clienteId]);

    useEffect(() => {
        if (accountsLoading) return;
        if (postAccounts.length === 0) {
            setLoading(false);
            setPosts([]);
            setCapabilities(null);
            return;
        }
        fetchAll();
    }, [fetchAll, accountsLoading, postAccounts.length]);

    // Volta pra primeira pagina sempre que o conjunto filtrado muda — senao a
    // pagina 3 de um filtro anterior pode ficar vazia no novo resultado.
    useEffect(() => {
        setPage(0);
        setSelectedIds(new Set());
    }, [statusFilter, platformFilter, accountFilter, search]);

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = (checked) => {
        setSelectedIds(checked ? new Set(posts.map((p) => p._id)) : new Set());
    };

    const allSelected = posts.length > 0 && selectedIds.size === posts.length;

    const pagedPosts = posts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const openCreate = (atDate, platform) => {
        const params = new URLSearchParams();
        if (atDate instanceof Date) params.set('data', atDate.toISOString());
        if (platform) params.set('platform', platform);
        const query = params.toString() ? `?${params.toString()}` : '';
        navigate(`${BASE_URL}/novo${query}`);
    };

    const openEdit = (post) => {
        setPreviewPost(null);
        navigate(`${BASE_URL}/${post._id}`);
    };

    // Clique no item da listagem: abre a pre-visualizacao rapida (modal), sem
    // sair da lista. A edicao completa continua no menu "Editar/Ver detalhes".
    const openPreview = (post) => {
        setPreviewPost(post);
    };

    const handleReconnect = () => {
        navigate('/dashboard/configuracoes/contas-sociais?from=publicacoes');
    };

    const handlePublish = async (post) => {
        try {
            const pub = await mktPostsService.publicar(post._id);
            if (pub.status === 202) {
                toast({ title: t('posts.toast.processing'), description: t('posts.toast.videoProcessing') });
            } else if (pub.data?.status === 'partially_published') {
                toast({ title: t('posts.toast.partialPublish'), description: t('posts.toast.partialPublishDesc'), variant: 'destructive' });
            } else {
                toast({ title: t('posts.toast.published') });
            }
            fetchAll();
        } catch (err) {
            if (err?.kind === 'MISSING_SCOPE') {
                toast({ title: t('posts.toast.insufficientScope'), description: t('posts.toast.reconnectPublish'), variant: 'destructive' });
            } else {
                toast({ title: t('posts.toast.publishFailed'), description: err?.message, variant: 'destructive' });
            }
            fetchAll();
        }
    };

    const handleRetry = async (post) => {
        try {
            await mktPostsService.retentar(post._id);
            toast({ title: t('posts.toast.retryStarted') });
            fetchAll();
        } catch (err) {
            toast({ title: t('posts.toast.retryFailed'), description: err?.message, variant: 'destructive' });
        }
    };

    const handleCancel = async (post) => {
        try {
            await mktPostsService.cancelar(post._id);
            toast({ title: t('posts.toast.scheduleCancelled') });
            fetchAll();
        } catch (err) {
            toast({ title: t('posts.toast.cancelFailed'), description: err?.message, variant: 'destructive' });
        }
    };

    const handleDeleteConfirm = async ({ remote }) => {
        setDeleting(true);
        try {
            await mktPostsService.deletar(user.clienteId, deleteTarget._id, { remote });
            toast({ title: t('posts.toast.deleted') });
            setDeleteTarget(null);
            fetchAll();
        } catch (err) {
            toast({ title: t('posts.toast.deleteFailed'), description: err?.message, variant: 'destructive' });
        } finally {
            setDeleting(false);
        }
    };

    const handleBulkDeleteConfirm = async ({ remote }) => {
        setBulkDeleting(true);
        try {
            if (bulkDeleteMode === 'account') {
                await mktPostsService.deletarEmMassa(user.clienteId, { accountId: accountFilter, remote });
            } else {
                await mktPostsService.deletarEmMassa(user.clienteId, { ids: Array.from(selectedIds), remote });
            }
            toast({ title: t('posts.toast.deleted') });
            setBulkDeleteMode(null);
            setSelectedIds(new Set());
            fetchAll();
        } catch (err) {
            toast({ title: t('posts.toast.deleteFailed'), description: err?.message, variant: 'destructive' });
        } finally {
            setBulkDeleting(false);
        }
    };

    const runSync = useCallback(async ({ silent = false, auto = false } = {}) => {
        if (syncLock.current) return null;
        syncLock.current = true;
        if (auto) setAutoSyncing(true);
        else setSyncing(true);
        try {
            const result = await mktPostsService.sincronizar(user.clienteId);
            const imported = result?.data?.imported ?? 0;
            const updated = result?.data?.updated ?? 0;
            const errors = result?.data?.errors || [];

            if (!silent) {
                if (errors.length > 0 && imported === 0 && updated === 0) {
                    toast({
                        title: t('posts.toast.syncFailed'),
                        description: errors[0]?.message || t('posts.toast.syncFailed'),
                        variant: 'destructive',
                    });
                } else if (errors.length > 0) {
                    toast({
                        title: t('posts.toast.synced'),
                        description: `${t('posts.toast.syncedDesc', { count: imported })} (${errors.length} conta(s) com erro: ${errors[0]?.message})`,
                        variant: 'destructive',
                    });
                } else if (imported > 0 || updated > 0) {
                    toast({ title: t('posts.toast.synced'), description: t('posts.toast.syncedDesc', { count: imported }) });
                } else {
                    toast({
                        title: t('posts.toast.nothingToSync'),
                        description: t('posts.toast.nothingToSyncDesc'),
                    });
                }
            } else if (auto && imported > 0) {
                toast({ title: t('posts.toast.synced'), description: t('posts.toast.syncedDesc', { count: imported }) });
            } else if (auto && errors.length > 0 && imported === 0 && updated === 0) {
                toast({
                    title: t('posts.toast.syncFailed'),
                    description: errors[0]?.message || t('posts.toast.syncFailed'),
                    variant: 'destructive',
                });
            }

            await fetchAll({ soft: true });
            return result;
        } catch (err) {
            if (!silent || auto) {
                toast({ title: t('posts.toast.syncFailed'), description: err?.message, variant: 'destructive' });
            }
            return null;
        } finally {
            syncLock.current = false;
            setAutoSyncing(false);
            setSyncing(false);
        }
    }, [user.clienteId, fetchAll, t]);

    const handleSync = () => runSync();

    useEffect(() => {
        if (loading || accountsLoading || syncLock.current) return;
        if (postAccounts.length === 0 || posts.length > 0 || hasActiveFilters) return;
        if (sessionStorage.getItem(autoSyncStorageKey)) return;

        sessionStorage.setItem(autoSyncStorageKey, '1');
        runSync({ silent: true, auto: true });
    }, [
        loading,
        accountsLoading,
        postAccounts.length,
        posts.length,
        hasActiveFilters,
        autoSyncStorageKey,
        runSync,
    ]);

    const renderHeader = (actions) => (
        <div className={viewMode === 'calendar' ? d.page.headerCalendar : d.page.stickyHeader}>
            <div className={d.page.container}>
                <div className={d.page.topbar}>
                    <div className={d.page.topbarIdentity}>
                        <div className={d.page.iconTile}>
                            <Iconify icon="eva:edit-2-outline" width={22} />
                        </div>
                        <div className="min-w-0">
                            <h1 className={d.page.headerTitle}>{t('posts.title')}</h1>
                            <p className={d.page.headerSubtitle}>{t('posts.subtitle')}</p>
                        </div>
                    </div>
                    {actions ? (
                        <div className={d.page.topbarActions}>{actions}</div>
                    ) : null}
                </div>
            </div>
        </div>
    );

    if (accountsLoading) {
        return (
            <div className={d.page.root} style={{ fontFamily: d.page.fontStack }}>
                {renderHeader(null)}
                <div className={`${d.page.container} pt-4 ${d.page.pageBody}`}>
                    <MktLoading text={t('common.loading')} />
                </div>
            </div>
        );
    }

    if (semContaParaPosts) {
        return (
            <div className={d.page.root} style={{ fontFamily: d.page.fontStack }}>
                {renderHeader(
                    <button type="button" className={d.page.primaryButton} onClick={handleReconnect}>
                        <Iconify icon="eva:link-2-outline" width={16} />
                        {t('posts.connect.noAccountCta')}
                    </button>
                )}
                <div className={`${d.page.container} pt-4 ${d.page.pageBody}`}>
                    <PostsConnectHero onConnect={handleReconnect} />
                </div>
            </div>
        );
    }

    const isCalendarView = viewMode === 'calendar';

    return (
        <div className={`${d.page.root} ${isCalendarView ? d.page.rootCalendar : ''}`} style={{ fontFamily: d.page.fontStack }}>
            {renderHeader(
                <>
                    <div className={d.page.viewToggle}>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('list')}
                                    className={viewMode === 'list' ? d.page.viewToggleBtnActive : d.page.viewToggleBtn}
                                    title={t('posts.viewMode.list')}
                                >
                                    <Iconify icon="eva:list-outline" width={17} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('grid')}
                                    className={viewMode === 'grid' ? d.page.viewToggleBtnActive : d.page.viewToggleBtn}
                                    title={t('posts.viewMode.grid')}
                                >
                                    <Iconify icon="eva:grid-outline" width={17} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('calendar')}
                                    className={viewMode === 'calendar' ? d.page.viewToggleBtnActive : d.page.viewToggleBtn}
                                    title={t('posts.viewMode.calendar')}
                                >
                                    <Iconify icon="eva:calendar-outline" width={17} />
                                </button>
                            </div>
                            <button
                                type="button"
                                className={d.page.secondaryButton}
                                onClick={handleSync}
                                disabled={syncing}
                            >
                                <Iconify icon={syncing ? 'eos-icons:loading' : 'eva:refresh-outline'} width={16} />
                                {t('posts.sync')}
                            </button>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button type="button" className={d.page.primaryButton}>
                                        <Iconify icon="eva:plus-fill" width={16} />
                                        {t('posts.new')}
                                        <Iconify icon="eva:chevron-down-fill" width={14} />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 rounded-xl">
                                    <DropdownMenuItem onClick={() => openCreate(undefined, 'instagram')}>
                                        <Iconify icon="skill-icons:instagram" width={16} className="mr-2" />
                                        {t('posts.platforms.instagram')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openCreate(undefined, 'facebook')}>
                                        <Iconify icon="mdi:facebook" width={16} className="mr-2 text-[#1877f2]" />
                                        {t('posts.platforms.facebook')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                </>
            )}

            <div className={`${d.page.container} ${isCalendarView ? d.page.pageBodyCalendar : `pt-4 ${d.page.pageBody}`}`}>
                <div className={`${d.page.filterBar} ${isCalendarView ? 'shrink-0 mb-3' : ''}`}>
                    <div className={d.page.filterItem}>
                        <span className={d.page.filterLabel}>{t('common.search', 'Buscar')}</span>
                        <div className={d.page.filterSearchWrap}>
                            <Iconify icon="eva:search-outline" width={15} className={d.page.searchIcon} />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('posts.searchPlaceholder')}
                                className={d.page.filterSearch}
                            />
                        </div>
                    </div>

                    <div className={d.page.filterItem}>
                        <span className={d.page.filterLabel}>{t('common.status')}</span>
                        <select
                            className={d.page.filterSelect}
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">{t('common.all')}</option>
                            {Object.entries(STATUS_MAP).map(([value, cfg]) => (
                                <option key={value} value={value}>{t(cfg.labelKey)}</option>
                            ))}
                        </select>
                    </div>

                    <div className={d.page.filterItem}>
                        <span className={d.page.filterLabel}>{t('common.platform')}</span>
                        <select
                            className={d.page.filterSelect}
                            value={platformFilter}
                            onChange={(e) => setPlatformFilter(e.target.value)}
                        >
                            <option value="">{t('common.allFeminine')}</option>
                            <option value="instagram">{t('posts.platforms.instagram')}</option>
                            <option value="facebook">{t('posts.platforms.facebook')}</option>
                        </select>
                    </div>

                    <div className={d.page.filterItem}>
                        <span className={d.page.filterLabel}>{t('common.account')}</span>
                        <Select value={accountFilter || 'all'} onValueChange={(value) => setAccountFilter(value === 'all' ? '' : value)}>
                            <SelectTrigger className={d.page.filterSelectWide}>
                                <SelectValue>
                                    {(() => {
                                        const selected = postAccounts.find((acc) => acc._id === accountFilter);
                                        const platformInfo = selected && resolvePlatform(selected.provider);
                                        return (
                                            <span className="flex items-center gap-1.5 truncate">
                                                {platformInfo && (
                                                    <Iconify icon={platformInfo.icon} width={13} style={{ color: platformInfo.color }} className="flex-shrink-0" />
                                                )}
                                                {selected ? selected.account_name : t('common.allFeminine')}
                                            </span>
                                        );
                                    })()}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="z-[9999]" position="popper" sideOffset={4}>
                                <SelectItem value="all">{t('common.allFeminine')}</SelectItem>
                                {postAccounts.map((acc) => {
                                    const platformInfo = resolvePlatform(acc.provider);
                                    return (
                                        <SelectItem key={acc._id} value={acc._id}>
                                            <span className="flex items-center gap-1.5">
                                                {platformInfo && (
                                                    <Iconify icon={platformInfo.icon} width={13} style={{ color: platformInfo.color }} />
                                                )}
                                                {acc.account_name}
                                            </span>
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

            <div className={isCalendarView ? 'shrink-0 mb-3' : undefined}>
                <PublishScopeBanner capabilities={capabilities} onReconnect={handleReconnect} />
            </div>

            {accountFilter && filteredAccount && !loading && (
                <div className={`${d.page.accountBar.container} ${isCalendarView ? 'shrink-0 mb-3' : ''}`}>
                    <div className={d.page.accountBar.identity}>
                        {(() => {
                            const platformInfo = resolvePlatform(filteredAccount.provider);
                            return platformInfo ? (
                                <Iconify icon={platformInfo.icon} width={16} style={{ color: platformInfo.color }} className="flex-shrink-0" />
                            ) : null;
                        })()}
                        <span className={d.page.accountBar.label}>
                            {t('posts.bulkBar.accountContext', { name: filteredAccount.account_name })}
                        </span>
                        <span className={d.page.accountBar.count}>
                            ({t('posts.bulkBar.postCount', { count: posts.length })})
                        </span>
                    </div>
                    <div className={d.page.accountBar.actions}>
                        <button
                            type="button"
                            className={d.page.accountBar.clearBtn}
                            onClick={() => setAccountFilter('')}
                        >
                            {t('posts.bulkBar.clearAccountFilter')}
                        </button>
                        <button
                            type="button"
                            className={d.page.accountBar.dangerBtn}
                            onClick={() => setBulkDeleteMode('account')}
                        >
                            <Iconify icon="eva:trash-2-outline" width={14} />
                            {t('posts.bulkBar.deleteAllAccount')}
                        </button>
                    </div>
                </div>
            )}

            {autoSyncing && (
                <div className={`mb-4 flex items-center gap-2.5 px-4 py-3 rounded-xl border border-violet-200 bg-violet-50 text-sm text-violet-800 ${isCalendarView ? 'shrink-0 mb-3' : ''}`}>
                    <Iconify icon="eos-icons:loading" width={18} className="text-[#7C3AED]" />
                    <span>{t('posts.empty.syncingTitle')}</span>
                </div>
            )}

            {selectedIds.size > 0 && (
                <div className={`${d.page.bulkBar.container} ${isCalendarView ? 'shrink-0 mb-3' : ''}`}>
                    <span className={d.page.bulkBar.label}>
                        {t('posts.bulkBar.selectedCount', { count: selectedIds.size })}
                    </span>
                    <div className={d.page.bulkBar.actions}>
                        <button
                            type="button"
                            className={d.page.bulkBar.clearBtn}
                            onClick={() => setSelectedIds(new Set())}
                        >
                            {t('posts.bulkBar.clearSelection')}
                        </button>
                        <button
                            type="button"
                            className={d.page.bulkBar.dangerBtn}
                            onClick={() => setBulkDeleteMode('selection')}
                        >
                            <Iconify icon="eva:trash-2-outline" width={14} />
                            {t('posts.bulkBar.deleteSelected')}
                        </button>
                    </div>
                </div>
            )}

            {/* Loading */}
            {loading && viewMode === 'calendar' && (
                <div className={d.page.calendarWrap}>
                    <MktLoading text={t('common.loading')} />
                </div>
            )}

            {loading && viewMode !== 'calendar' && (
                <div className="flex items-center justify-center py-20">
                    <div className="w-48 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full w-1/2 bg-[#7C3AED] rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" />
                    </div>
                </div>
            )}

            {/* List View */}
            {!loading && viewMode === 'list' && posts.length > 0 && (
                <>
                    <MktPostsTable
                        posts={pagedPosts}
                        capabilities={capabilities}
                        onOpen={openEdit}
                        onPreview={openPreview}
                        onDelete={setDeleteTarget}
                        onPublish={handlePublish}
                        onRetry={handleRetry}
                        onCancel={handleCancel}
                        selectedIds={selectedIds}
                        onToggleOne={toggleSelectOne}
                        onToggleAll={toggleSelectAll}
                        allSelected={allSelected}
                    />

                    {posts.length > rowsPerPage && (
                        <div className="flex flex-wrap justify-between items-center gap-3 p-4 mt-3 rounded-xl border border-border bg-card">
                            <span className="text-sm text-muted-foreground tabular-nums">
                                {t('common.pagination.showing', {
                                    from: page * rowsPerPage + 1,
                                    to: Math.min((page + 1) * rowsPerPage, posts.length),
                                    total: posts.length
                                })}
                            </span>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">{t('common.pagination.rows')}</span>
                                    <select
                                        className="h-8 text-xs rounded-lg border border-border bg-card px-2"
                                        value={rowsPerPage}
                                        onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                                <div className="flex gap-1">
                                    <button
                                        type="button"
                                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                                        disabled={page === 0}
                                        className="h-8 w-8 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-accent disabled:opacity-40 disabled:pointer-events-none"
                                    >
                                        <Iconify icon="mdi:chevron-left" width={20} height={20} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPage((p) => Math.min(Math.ceil(posts.length / rowsPerPage) - 1, p + 1))}
                                        disabled={page >= Math.ceil(posts.length / rowsPerPage) - 1}
                                        className="h-8 w-8 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-accent disabled:opacity-40 disabled:pointer-events-none"
                                    >
                                        <Iconify icon="mdi:chevron-right" width={20} height={20} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Grid View */}
            {!loading && viewMode === 'grid' && posts.length > 0 && (
                <MktPostsGrid
                    posts={posts}
                    capabilities={capabilities}
                    onOpen={openEdit}
                    onPreview={openPreview}
                    onDelete={setDeleteTarget}
                    onPublish={handlePublish}
                    onRetry={handleRetry}
                    onCancel={handleCancel}
                    selectedIds={selectedIds}
                    onToggleOne={toggleSelectOne}
                />
            )}

            {/* Calendar View */}
            {!loading && viewMode === 'calendar' && (
                <div className={d.page.calendarWrap}>
                    <MktPostsCalendar posts={posts} onOpenPost={openEdit} onCreateAt={openCreate} />
                </div>
            )}

            {/* Empty State */}
            {!loading && (viewMode === 'list' || viewMode === 'grid') && posts.length === 0 && (
                autoSyncing ? (
                    <MktLoading text={t('posts.empty.syncingSubtitle')} />
                ) : (
                    <div className={d.emptyState.container}>
                        <div className={d.emptyState.iconBox}>
                            <Iconify icon="eva:file-text-outline" width={28} className="text-violet-600" />
                        </div>
                        <h3 className={d.emptyState.title}>
                            {hasActiveFilters ? t('posts.empty.filteredTitle') : t('posts.empty.title')}
                        </h3>
                        <p className={d.emptyState.subtitle}>
                            {hasActiveFilters ? t('posts.empty.filteredSubtitle') : t('posts.empty.subtitle')}
                        </p>
                        <div className="flex flex-col sm:flex-row items-center gap-3 mt-1">
                            {!hasActiveFilters && (
                                <button
                                    type="button"
                                    className={d.page.secondaryButton}
                                    onClick={handleSync}
                                    disabled={syncing}
                                >
                                    <Iconify icon={syncing ? 'eos-icons:loading' : 'eva:refresh-outline'} width={16} />
                                    {syncing ? t('posts.syncing') : t('posts.empty.syncAction')}
                                </button>
                            )}
                            <span className={d.emptyState.action} onClick={() => openCreate()}>
                                {t('posts.empty.action')}
                            </span>
                        </div>
                    </div>
                )
            )}

            <MktPostPreviewModal
                open={Boolean(previewPost)}
                post={previewPost}
                accounts={accounts}
                onClose={() => setPreviewPost(null)}
                onEdit={openEdit}
                onPublish={(post) => { setPreviewPost(null); handlePublish(post); }}
                onDelete={(post) => { setPreviewPost(null); setDeleteTarget(post); }}
            />

            <MktPostDeleteDialog
                open={Boolean(deleteTarget)}
                post={deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
            />

            <MktPostsBulkDeleteDialog
                open={Boolean(bulkDeleteMode)}
                count={selectedIds.size}
                accountName={bulkDeleteMode === 'account' ? accounts.find((a) => a._id === accountFilter)?.account_name : null}
                onClose={() => setBulkDeleteMode(null)}
                onConfirm={handleBulkDeleteConfirm}
                deleting={bulkDeleting}
            />
            </div>
        </div>
    );
}
